// Shared helpers for the customer license portal + medflow fleet view.
// The two pages render the same environments table with different
// scoping, so the column logic, status derivation, and time formatters
// all live here to avoid drift.

export type FleetLiveness = 'online' | 'stale' | 'offline' | 'unknown'

// Map a lastSeenAt timestamp to a coarse liveness bucket. Matches the
// server's /api/fleet/stats thresholds (2 min online, 10 min stale).
// Keep the thresholds in sync if the server ever tunes its window.
export function livenessFromLastSeen(lastSeenAt: string | null, now: Date = new Date()): FleetLiveness {
  if (!lastSeenAt) return 'unknown'
  const ageSec = (now.getTime() - new Date(lastSeenAt).getTime()) / 1000
  if (ageSec < 0) return 'online'
  if (ageSec < 120) return 'online'
  if (ageSec < 600) return 'stale'
  return 'offline'
}

// Classification for the per-device badge on the Fleet Device Status page.
// UDM emits exactly three status values in its heartbeat payload
// (see UnifiedDeviceManagerService.SendAlphaEdgeHeartbeatAsync in the
// Alpha-Instruments repo): 'connected', 'disconnected', 'disabled'.
// Disabled devices are intentionally included in the heartbeat so Central
// sees the full fleet, which is why we need an explicit 'disabled' state
// here instead of treating recent heartbeats as proof the device is active.
export type DeviceBadgeHealth = 'active' | 'stale' | 'offline' | 'disabled'

export function classifyDeviceBadge(
  lastHeartbeatAt: string,
  status: string,
  now: Date = new Date(),
): DeviceBadgeHealth {
  const ageMin = (now.getTime() - new Date(lastHeartbeatAt).getTime()) / 60000

  // Stale heartbeat means the tray itself is unreachable -- the last
  // per-device status it reported is no longer trustworthy, so the tray's
  // liveness takes precedence.
  if (ageMin >= 30) return 'offline'
  if (ageMin >= 10) return 'stale'

  const lower = status.toLowerCase()
  if (lower === 'disabled') return 'disabled'
  if (lower.includes('error') || lower.includes('disconnected')) return 'offline'
  return 'active'
}

// Relative-time formatter used in list + detail views. Short strings
// ("12s", "5m", "3h", "2d") so they fit inside a DataTable cell.
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—'
  const diffMs = now.getTime() - new Date(iso).getTime()
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// Alert display helpers (Phase 5.3.B)

const ALERT_TYPE_LABELS: Record<string, string> = {
  stale_deployment: 'Stale Deployment',
  expiring_license: 'Expiring License',
  container_down: 'Container Down',
  log_error_burst: 'Log Error Burst',
  container_crash: 'Container Crash',
  db_connection_failed: 'DB Connection Failed',
}

const ALERT_SEVERITY_LABELS: Record<string, string> = {
  warning: 'Warning',
  critical: 'Critical',
}

export function alertTypeLabel(type: string): string {
  return ALERT_TYPE_LABELS[type] ?? type
}

export function alertSeverityLabel(severity: string): string {
  return ALERT_SEVERITY_LABELS[severity] ?? severity
}

// Response shape returned by GET /api/fleet/environments. Lives here so
// both pages share one source of truth.
export interface EnvironmentRow {
  readonly id: string
  // Nullable: a shared multi-tenant environment has no single owner org
  // (the operator-org pointer is optional). Per-org licensing lives on
  // tenancies, not on the environment.
  readonly ownerOrganizationId: string | null
  readonly ownerOrganizationName: string | null
  readonly name: string
  readonly environmentType: 'edge' | 'cloud' | 'other'
  // Licensing moved off the Environment onto Tenancy (the license
  // source-of-truth). An Environment hosts 1..N tenancies, so it no longer
  // carries a single license status/tier/expiry/features. See the Tenancies
  // section on the env detail page (medflow_admin).
  readonly currentVersion: string | null
  readonly lastSeenAt: string | null
  readonly enrolledAt: string
  readonly notes: string | null
  readonly isArchived: boolean
  readonly analyticsDeletionSlaDays: number
  // True when an AlphaEdge edge stack is (or will be) reporting in via
  // /api/fleet/report. False for split-mode / instrument-monitoring envs
  // where UDM hits Central directly and the edge-derived alert evaluators
  // should skip this env. Flipped by medflow_admin from the env detail
  // page when AlphaEdge is later deployed for the customer.
  readonly expectsEdgeReport: boolean
  readonly publicIp: string | null
  readonly provisioningStatus: string | null
}
